import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  BookOpen, Calendar, Clock, User, Settings, LogOut, 
  GraduationCap, MessageCircle, Award, TrendingUp, Play,
  FileText, CheckCircle, AlertCircle
} from 'lucide-react';
import { useUserAuth } from '../../context/UserAuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { fadeInUp, transitionMedium } from '../../utils/animations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Get locale string from language code
const getLocale = (lang) => {
  const locales = { ko: 'ko-KR', ja: 'ja-JP', en: 'en-US' };
  return locales[lang] || locales.en;
};

const translations = {
  en: {
    welcome: 'Welcome back',
    dashboard: 'Student Dashboard',
    upcomingClasses: 'Upcoming Classes',
    noClasses: 'No upcoming classes scheduled',
    scheduleClass: 'Schedule a Class',
    learningProgress: 'Learning Progress',
    lessonsCompleted: 'Lessons Completed',
    hoursLearned: 'Hours Learned',
    currentLevel: 'Current Level',
    quickActions: 'Quick Actions',
    bookClass: 'Book a Class',
    mySchedule: 'My Schedule',
    viewProgress: 'View Progress',
    homework: 'Homework',
    materials: 'Materials',
    teacherNotes: 'Teacher Notes',
    settings: 'Settings',
    logout: 'Logout',
    startLearning: 'Start Learning Today',
    startLearningDesc: 'Book your first class and begin your English learning journey.',
    bookFirstClass: 'Book Your First Class',
    pendingHomework: 'Pending Homework',
    noHomework: 'No pending homework',
    viewAll: 'View All',
    recentNotes: 'Recent Teacher Notes',
    noNotes: 'No teacher notes yet',
    classHistory: 'Class History',
    noHistory: 'No completed classes yet',
    with: 'with',
    due: 'Due',
    loading: 'Loading...'
  },
  ko: {
    welcome: '돌아오신 것을 환영합니다',
    dashboard: '학생 대시보드',
    upcomingClasses: '예정된 수업',
    noClasses: '예정된 수업이 없습니다',
    scheduleClass: '수업 예약하기',
    learningProgress: '학습 진행률',
    lessonsCompleted: '완료한 수업',
    hoursLearned: '학습 시간',
    currentLevel: '현재 레벨',
    quickActions: '빠른 작업',
    bookClass: '수업 예약',
    mySchedule: '내 일정',
    viewProgress: '진행률 보기',
    homework: '숙제',
    materials: '학습 자료',
    teacherNotes: '선생님 노트',
    settings: '설정',
    logout: '로그아웃',
    startLearning: '오늘부터 학습을 시작하세요',
    startLearningDesc: '첫 수업을 예약하고 영어 학습 여정을 시작하세요.',
    bookFirstClass: '첫 수업 예약하기',
    pendingHomework: '진행 중인 숙제',
    noHomework: '진행 중인 숙제가 없습니다',
    viewAll: '전체 보기',
    recentNotes: '최근 선생님 노트',
    noNotes: '선생님 노트가 없습니다',
    classHistory: '수업 기록',
    noHistory: '완료한 수업이 없습니다',
    with: '선생님:',
    due: '마감일',
    loading: '로딩 중...'
  },
  ja: {
    welcome: 'おかえりなさい',
    dashboard: '生徒ダッシュボード',
    upcomingClasses: '予定のレッスン',
    noClasses: '予定されているレッスンはありません',
    scheduleClass: 'レッスンを予約する',
    learningProgress: '学習進捗',
    lessonsCompleted: '完了したレッスン',
    hoursLearned: '学習時間',
    currentLevel: '現在のレベル',
    quickActions: 'クイックアクション',
    bookClass: 'レッスン予約',
    mySchedule: 'スケジュール',
    viewProgress: '進捗を見る',
    homework: '宿題',
    materials: '学習教材',
    teacherNotes: '講師のメモ',
    settings: '設定',
    logout: 'ログアウト',
    startLearning: '今日から学習を始めましょう',
    startLearningDesc: '最初のレッスンを予約して、英語学習の旅を始めましょう。',
    bookFirstClass: '最初のレッスンを予約',
    pendingHomework: '未完了の宿題',
    noHomework: '未完了の宿題はありません',
    viewAll: 'すべて見る',
    recentNotes: '最近の講師メモ',
    noNotes: '講師のメモはまだありません',
    classHistory: 'レッスン履歴',
    noHistory: '完了したレッスンはありません',
    with: '講師:',
    due: '期限',
    loading: '読み込み中...'
  }
};

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useUserAuth();
  const { language } = useLanguage();
  const t = translations[language] || translations.en;

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/student/dashboard`,
          { withCredentials: true }
        );
        setDashboardData(response.data);
      } catch (error) {
        // Dashboard fetch failed - show empty state
        setDashboardData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Extract data from dashboard response
  const progress = dashboardData?.progress || { lessons_completed: 0, total_hours: 0, current_level: 'beginner' };
  const upcomingClasses = dashboardData?.upcoming_classes || [];
  const pendingHomework = dashboardData?.pending_homework || [];
  const recentNotes = dashboardData?.recent_notes || [];
  const classHistory = dashboardData?.class_history || [];
  const materials = dashboardData?.materials || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4" />
          <p className="text-slate-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="student-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-xl font-bold text-slate-900">English Passion</Link>
              <span className="text-sm text-slate-500 hidden sm:inline" data-testid="dashboard-type">{t.dashboard}</span>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/settings')}
                className="p-2 text-slate-500 hover:text-slate-900 transition-colors"
                data-testid="settings-btn"
              >
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
                <span className="hidden sm:inline">{t.logout}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <motion.div
          initial={fadeInUp.initial}
          animate={fadeInUp.animate}
          transition={transitionMedium}
          className="mb-8"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            {t.welcome}, {user?.name}!
          </h1>
          <p className="text-slate-600">
            {new Date().toLocaleDateString(getLocale(language), {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Start Learning CTA (for new students) */}
            {progress.lessons_completed === 0 && (
              <motion.div
                initial={fadeInUp.initial}
                animate={fadeInUp.animate}
                transition={{ delay: 0.1 }}
                data-testid="start-learning-cta"
                className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 text-white"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Play className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2 text-white">{t.startLearning}</h3>
                    <p className="text-slate-300 mb-4">{t.startLearningDesc}</p>
                    <Link
                      to="/schedule"
                      data-testid="book-first-class-btn"
                      className="inline-flex items-center gap-2 bg-white text-slate-900 px-5 py-2.5 rounded-lg font-medium hover:bg-slate-100 transition-colors"
                    >
                      <Calendar className="w-4 h-4" />
                      {t.bookFirstClass}
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Upcoming Classes */}
            <motion.div
              initial={fadeInUp.initial}
              animate={fadeInUp.animate}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">{t.upcomingClasses}</h2>
                <Link
                  to="/schedule"
                  className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
                >
                  {t.scheduleClass} →
                </Link>
              </div>
              
              {upcomingClasses.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{t.noClasses}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingClasses.map((cls) => (
                    <div key={cls.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                      <div className="w-12 h-12 bg-slate-200 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-slate-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">
                          {t.with} {cls.teacher_name}
                        </p>
                        <p className="text-sm text-slate-500">
                          {new Date(cls.scheduled_time).toLocaleString(getLocale(language), {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        cls.status === 'confirmed' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {cls.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Pending Homework */}
            <motion.div
              initial={fadeInUp.initial}
              animate={fadeInUp.animate}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">{t.pendingHomework}</h2>
                <Link
                  to="/homework"
                  className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
                >
                  {t.viewAll} →
                </Link>
              </div>
              
              {pendingHomework.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
                  <p className="text-slate-500">{t.noHomework}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingHomework.map((hw) => (
                    <div key={hw.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{hw.title}</p>
                        {hw.due_date && (
                          <p className="text-sm text-slate-500">
                            {t.due}: {new Date(hw.due_date).toLocaleDateString(getLocale(language))}
                          </p>
                        )}
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        hw.status === 'submitted' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {hw.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Recent Teacher Notes */}
            <motion.div
              initial={fadeInUp.initial}
              animate={fadeInUp.animate}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <h2 className="text-lg font-semibold text-slate-900 mb-4">{t.recentNotes}</h2>
              
              {recentNotes.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{t.noNotes}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentNotes.map((note) => (
                    <div key={note.id} className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-900">
                          {note.teacher_name || 'Teacher'}
                        </span>
                        {note.performance_rating && (
                          <span className="text-amber-500">
                            {'★'.repeat(note.performance_rating)}{'☆'.repeat(5 - note.performance_rating)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{note.notes}</p>
                      {note.class_date && (
                        <p className="text-xs text-slate-400 mt-2">
                          {new Date(note.class_date).toLocaleDateString(getLocale(language))}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Learning Progress */}
            <motion.div
              initial={fadeInUp.initial}
              animate={fadeInUp.animate}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <h2 className="text-lg font-semibold text-slate-900 mb-4">{t.learningProgress}</h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <BookOpen className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{progress.lessons_completed}</p>
                  <p className="text-sm text-slate-600">{t.lessonsCompleted}</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <Clock className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{progress.total_hours}</p>
                  <p className="text-sm text-slate-600">{t.hoursLearned}</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-xl">
                  <TrendingUp className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900 capitalize">{progress.current_level}</p>
                  <p className="text-sm text-slate-600">{t.currentLevel}</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Profile Card */}
            <motion.div
              initial={fadeInUp.initial}
              animate={fadeInUp.animate}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="font-semibold text-slate-900">{user?.name}</h3>
                <p className="text-sm text-slate-500">{user?.email}</p>
                <div className="flex items-center justify-center gap-1 mt-2">
                  <GraduationCap className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500 capitalize">{user?.role}</span>
                </div>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={fadeInUp.initial}
              animate={fadeInUp.animate}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <h3 className="font-semibold text-slate-900 mb-4">{t.quickActions}</h3>
              <div className="space-y-2">
                <Link
                  to="/schedule"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                  data-testid="quick-book-class"
                >
                  <Calendar className="w-5 h-5 text-slate-500" />
                  <span className="text-slate-700">{t.bookClass}</span>
                </Link>
                <Link
                  to="/my-classes"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <BookOpen className="w-5 h-5 text-slate-500" />
                  <span className="text-slate-700">{t.mySchedule}</span>
                </Link>
                <Link
                  to="/homework"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <FileText className="w-5 h-5 text-slate-500" />
                  <span className="text-slate-700">{t.homework}</span>
                </Link>
                <Link
                  to="/materials"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <Award className="w-5 h-5 text-slate-500" />
                  <span className="text-slate-700">{t.materials}</span>
                </Link>
              </div>
            </motion.div>

            {/* Materials Preview */}
            {materials.length > 0 && (
              <motion.div
                initial={fadeInUp.initial}
                animate={fadeInUp.animate}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-2xl shadow-sm p-6"
              >
                <h3 className="font-semibold text-slate-900 mb-4">{t.materials}</h3>
                <div className="space-y-2">
                  {materials.slice(0, 3).map((material) => (
                    <a
                      key={material.id}
                      href={material.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                    >
                      <FileText className="w-5 h-5 text-blue-500" />
                      <span className="text-slate-700 text-sm truncate">{material.title}</span>
                    </a>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudentDashboard;
